@if (isLoading) {
  <div class="spinner-container">
    <div class="spinner"></div>
  </div>
} @else {
  <div class="synopsis">
    <!-- Profile Section -->
    <div class="profile-section">
      <app-profile-table
        [profileInfo]="profileInfo"
        [metrics]="metrics"
        [showEditButton]="true"
        [redBasicInfo]="redBasicInfo"
        [yellowBasicInfo]="yellowBasicInfo"
        (epicUserSelected)="epicUserSelected()"
      ></app-profile-table>
    </div>

    <!-- Basic Metrics Section -->
    <div class="metrics-section mt-4">
      <div class="table-container">
        <table class="metrics-table fixed-table align-middle text-center">
          <thead class="table-light">
            <tr>
              <th scope="col"></th>
              <th scope="col">Basic Metrics</th>
            </tr>
          </thead>
          <tbody>
            <!-- YES rows and expanded rows for Basic Metrics -->
            <ng-container *ngFor="let metric of groupedBasicDonorMetrics.yes; trackBy: trackByMetric">
              <tr>
                <td class="text-start ps-4 width-20pt">
                  {{ metric.label }}
                </td>
                <td [ngClass]="getBasicMetricValueClass(metric)">
                  <span class="metric-value-with-icon">
                    <span>{{ metric.value }}</span>
                    @if (isMetricTranslated(metric.key)) {
                      <i
                        class="bi bi-translate translated-metric-icon"
                        ngbTooltip="Translated to English"
                        placement="top"
                        container="body"
                        aria-label="Translated to English"
                      ></i>
                    }
                  </span>
                </td>
              </tr>
            </ng-container>

            <!-- NO block (only if there are any) -->
            <ng-container *ngIf="groupedBasicDonorMetrics.no.length > 0">
              <tr
                *ngFor="
                  let metric of groupedBasicDonorMetrics.no;
                  let i = index; trackBy: trackByMetric
                "
              >
                <td class="text-start ps-4 width-20pt">
                  {{ metric.label }}
                </td>
                <td
                  *ngIf="i === 0"
                  [attr.rowspan]="groupedBasicDonorMetrics.no.length"
                  class="text-center last-col"
                  [class.na-text]="isDonorBasicHistoryNA"
                  [style.background-color]="
                    isDonorBasicHistoryNA ? '#f2f2f2' : '#ecfef3'
                  "
                >
                  @if (isDonorBasicHistoryNA) {
                    Not filled out
                  } @else {
                    None
                  }
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Fitness Metrics Section -->
    <div class="metrics-section mt-4">
      <div class="table-container">
        <table class="metrics-table fixed-table align-middle text-center">
          <thead class="table-light">
            <tr>
              <th scope="col"></th>
              <th scope="col">Physical and Mental Health</th>
            </tr>
          </thead>
          <tbody>
            <!-- YES rows and expanded rows for Fitness -->
            <ng-container
              *ngFor="let metric of groupedPhyscialAndMentalDonorMetrics.yes; trackBy: trackByMetric"
            >
              <tr
                [class.clickable-row]="isMetricExpandable(metric.key)"
                (click)="
                  isMetricExpandable(metric.key)
                    ? toggleRowExpansion(metric.key)
                    : null
                "
              >
                <td class="text-start ps-4 width-20pt">
                  {{ metric.label }}
                </td>
                <td [ngClass]="{ 'background-light-yellow': !shouldShowSlidingScale(metric.key) && !isRiskScreening(metric.key) }"
                  [style.background-color]="
                    isRiskScreening(metric.key)
                      ? getRiskScreeningDisplayText(metric.value) ===
                        'Risk Identified'
                        ? '#fffacd'
                        : getRiskScreeningDisplayText(metric.value) ===
                            'Cleared'
                          ? '#ecfef3'
                          : ''
                      : ''
                  "
                >
                  @if (shouldShowSlidingScale(metric.key)) {
                    <div class="d-flex flex-column align-items-center">
                      <app-sliding-scale
                        [value]="getMetricValue(metric.key)"
                        [displayText]="metric.value"
                        [config]="getScaleConfig(metric.key)!"
                        size="medium"
                      >
                      </app-sliding-scale>
                    </div>
                  } @else {
                    <!-- Risk Screening custom text -->
                    @if (isRiskScreening(metric.key)) {
                      {{ getRiskScreeningDisplayText(metric.value) }}
                    } @else {
                      {{ metric.value }}
                    }
                  }
                  <i
                    *ngIf="isMetricExpandable(metric.key)"
                    class="bi ms-2 align-middle row-expand-toggle"
                    [class.bi-chevron-down]="!isRowExpanded(metric.key)"
                    [class.bi-chevron-up]="isRowExpanded(metric.key)"
                    (click)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    (keydown.enter)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    (keydown.space)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    role="button"
                    tabindex="0"
                  ></i>
                  <div
                    *ngIf="isRowExpanded(metric.key)"
                    class="mt-3 expanded-response-content"
                    (click)="$event.stopPropagation()"
                  >
                    <div
                      *ngIf="isLoadingResponses(metric.key)"
                      class="text-center"
                    >
                      <div
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></div>
                      Loading detailed responses...
                    </div>
                    <div
                      *ngIf="
                        !isLoadingResponses(metric.key) &&
                        getResponsesForMetric(metric.key).length > 0
                      "
                    >
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                            <tr>
                              <th class="subtable-question">Question</th>
                              <th class="subtable-answer">Answer</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              *ngFor="
                                let response of getResponsesForMetric(
                                  metric.key
                                ); trackBy: trackByResponse
                              "
                              [style.background-color]="
                                isAnswerYes(response.answerText)
                                  ? '#fff3cd'
                                  : ''
                              "
                            >
                              <td
                                class="subtable-question"
                                [style.color]="
                                  isMetricNoValue(response.answerText)
                                    ? '#929292'
                                    : ''
                                "
                              >
                                {{ response.questionText }}
                              </td>
                              <td
                                class="subtable-answer"
                                [style.color]="
                                  isMetricNoValue(response.answerText)
                                    ? '#929292'
                                    : ''
                                "
                              >
                                <div
                                  class="subtable-answer-text"
                                  [textContent]="
                                    getAnswerDisplayText(response) || 'N/A'
                                  "
                                ></div>

                                @if (canTranslateDetailedResponse(response)) {
                                  <div class="translation-tools">
                                    @if (
                                      getDetailedTranslationState(response)
                                        ?.translatedText
                                    ) {
                                      <div class="translated-answer">
                                        <div class="translated-answer-label">
                                          English translation
                                        </div>
                                        <div
                                          class="translated-answer-text"
                                          [textContent]="
                                            getDetailedTranslatedDisplayText(
                                              response
                                            )
                                          "
                                        ></div>
                                      </div>
                                    } @else {
                                      <button
                                        type="button"
                                        class="btn btn-sm translate-answer-btn"
                                        [disabled]="
                                          getDetailedTranslationState(response)
                                            ?.loading
                                        "
                                        (click)="
                                          translateDetailedResponse(response)
                                        "
                                      >
                                        @if (
                                          getDetailedTranslationState(response)
                                            ?.loading
                                        ) {
                                          Translating...
                                        } @else {
                                          Translate to English
                                        }
                                      </button>
                                    }

                                    @if (
                                      getDetailedTranslationState(response)
                                        ?.error
                                    ) {
                                      <div class="translation-error">
                                        {{
                                          getDetailedTranslationState(response)
                                            ?.error
                                        }}
                                      </div>
                                    }
                                  </div>
                                }
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      *ngIf="
                        !isLoadingResponses(metric.key) &&
                        getResponsesForMetric(metric.key).length === 0
                      "
                      class="text-muted"
                    >
                      No detailed responses available for this metric.
                    </div>
                  </div>
                </td>
              </tr>
            </ng-container>

            <!-- NO block (only if there are any) -->
            <ng-container
              *ngIf="groupedPhyscialAndMentalDonorMetrics.no.length > 0"
            >
              <!-- Show Risk Screening as its own row only when it has a value -->
              <ng-container *ngIf="riskFitnessPartition.riskRow as riskMetric">
                <tr>
                  <td class="text-start ps-4 width-20pt">
                    {{ riskMetric.label }}
                  </td>
                  <td
                    class="text-center"
                    [style.background-color]="
                      getRiskScreeningDisplayText(riskMetric.value) === 'Cleared'
                        ? '#ecfef3'
                        : getRiskScreeningDisplayText(riskMetric.value) === 'Risk Identified'
                          ? '#fffacd'
                          : ''
                    "
                  >
                    {{ getRiskScreeningDisplayText(riskMetric.value) }}
                  </td>
                </tr>
              </ng-container>

              <!-- Other cleared metrics grouped as None -->
              <ng-container
                *ngIf="getClearedNoMetrics(riskFitnessPartition.rest) as clearedFitnessNoMetrics"
              >
                <tr
                  *ngFor="
                    let metric of clearedFitnessNoMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="clearedFitnessNoMetrics.length"
                    class="text-center last-col"
                    style="background-color: #ecfef3"
                  >
                    None
                  </td>
                </tr>
              </ng-container>

              <!-- Missing metrics grouped separately -->
              <ng-container
                *ngIf="getNotFilledNoMetrics(riskFitnessPartition.rest) as notFilledFitnessMetrics"
              >
                <tr
                  *ngFor="
                    let metric of notFilledFitnessMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="notFilledFitnessMetrics.length"
                    style="background-color: #f2f2f2"
                    class="text-center last-col na-text"
                  >
                    Not filled out
                  </td>
                </tr>
              </ng-container> </ng-container
            >
          </tbody>
        </table>
      </div>
    </div>

    <!-- Health History Metrics Section -->
    <div class="metrics-section mt-4">
      <div class="table-container">
        <table class="metrics-table fixed-table align-middle text-center">
          <thead class="table-light">
            <tr>
              <th scope="col"></th>
              <th scope="col">Health History</th>
            </tr>
          </thead>
          <tbody>
            <!-- YES rows and expanded rows for Health History -->
            <ng-container
              *ngFor="
                let metric of filteredGroupedHealthHistoryDonorMetrics.yes; trackBy: trackByMetric
              "
            >
              <tr
                [class.clickable-row]="isMetricExpandable(metric.key)"
                (click)="
                  isMetricExpandable(metric.key)
                    ? toggleRowExpansion(metric.key)
                    : null
                "
              >
                <td class="text-start ps-4 width-20pt">
                  {{ metric.label }}
                </td>
                <td class="background-light-yellow">
                  {{ metric.value }}
                  <i
                    *ngIf="isMetricExpandable(metric.key)"
                    class="bi ms-2 align-middle row-expand-toggle"
                    [class.bi-chevron-down]="!isRowExpanded(metric.key)"
                    [class.bi-chevron-up]="isRowExpanded(metric.key)"
                    (click)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    (keydown.enter)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    (keydown.space)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    role="button"
                    tabindex="0"
                  ></i>
                  <div
                    *ngIf="isRowExpanded(metric.key)"
                    class="mt-3 expanded-response-content"
                    (click)="$event.stopPropagation()"
                  >
                    <div
                      *ngIf="isLoadingResponses(metric.key)"
                      class="text-center"
                    >
                      <div
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></div>
                      Loading detailed responses...
                    </div>
                    <div
                      *ngIf="
                        !isLoadingResponses(metric.key) &&
                        getResponsesForMetric(metric.key).length > 0
                      "
                    >
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                            <tr>
                              <th class="subtable-question">Question</th>
                              <th class="subtable-answer">Answer</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              *ngFor="
                                let response of getResponsesForMetric(
                                  metric.key
                                ); trackBy: trackByResponse
                              "
                              [style.background-color]="
                                isAnswerYes(response.answerText)
                                  ? '#fff3cd'
                                  : ''
                              "
                            >
                              <td
                                class="subtable-question"
                                [style.color]="
                                  isMetricNoValue(response.answerText)
                                    ? '#929292'
                                    : ''
                                "
                              >
                                {{ response.questionText }}
                              </td>
                              <td
                                class="subtable-answer"
                                [style.color]="
                                  isMetricNoValue(response.answerText)
                                    ? '#929292'
                                    : ''
                                "
                              >
                                <div
                                  class="subtable-answer-text"
                                  [textContent]="
                                    getAnswerDisplayText(response) || 'N/A'
                                  "
                                ></div>

                                @if (canTranslateDetailedResponse(response)) {
                                  <div class="translation-tools">
                                    @if (
                                      getDetailedTranslationState(response)
                                        ?.translatedText
                                    ) {
                                      <div class="translated-answer">
                                        <div class="translated-answer-label">
                                          English translation
                                        </div>
                                        <div
                                          class="translated-answer-text"
                                          [textContent]="
                                            getDetailedTranslatedDisplayText(
                                              response
                                            )
                                          "
                                        ></div>
                                      </div>
                                    } @else {
                                      <button
                                        type="button"
                                        class="btn btn-sm translate-answer-btn"
                                        [disabled]="
                                          getDetailedTranslationState(response)
                                            ?.loading
                                        "
                                        (click)="
                                          translateDetailedResponse(response)
                                        "
                                      >
                                        @if (
                                          getDetailedTranslationState(response)
                                            ?.loading
                                        ) {
                                          Translating...
                                        } @else {
                                          Translate to English
                                        }
                                      </button>
                                    }

                                    @if (
                                      getDetailedTranslationState(response)
                                        ?.error
                                    ) {
                                      <div class="translation-error">
                                        {{
                                          getDetailedTranslationState(response)
                                            ?.error
                                        }}
                                      </div>
                                    }
                                  </div>
                                }
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      *ngIf="
                        !isLoadingResponses(metric.key) &&
                        getResponsesForMetric(metric.key).length === 0
                      "
                      class="text-muted"
                    >
                      No detailed responses available for this metric.
                    </div>
                  </div>
                </td>
              </tr>
            </ng-container>

            <!-- NO block (only if there are any) -->
            <ng-container
              *ngIf="groupedHealthHistoryDonorMetrics.no.length > 0"
            >
              <ng-container
                *ngIf="getClearedNoMetrics(filteredGroupedHealthHistoryDonorMetrics.no) as clearedHealthNoMetrics"
              >
                <tr
                  *ngFor="
                    let metric of clearedHealthNoMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="clearedHealthNoMetrics.length"
                    class="text-center last-col"
                    style="background-color: #ecfef3"
                  >
                    None
                  </td>
                </tr>
              </ng-container>

              <ng-container
                *ngIf="getNotFilledNoMetrics(filteredGroupedHealthHistoryDonorMetrics.no) as notFilledHealthMetrics"
              >
                <tr
                  *ngFor="
                    let metric of notFilledHealthMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="notFilledHealthMetrics.length"
                    class="text-center last-col na-text"
                    style="background-color: #f2f2f2"
                  >
                    Not filled out
                  </td>
                </tr>
              </ng-container>

              <ng-container
                *ngIf="getNotRequiredNoMetrics(filteredGroupedHealthHistoryDonorMetrics.no) as notRequiredHealthMetrics"
              >
                <tr
                  *ngFor="
                    let metric of notRequiredHealthMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="notRequiredHealthMetrics.length"
                    class="text-center last-col na-text"
                    style="background-color: #f2f2f2"
                  >
                    Not required
                  </td>
                </tr>
              </ng-container>
            </ng-container>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Family History Metrics Section -->
    <div class="metrics-section mt-4">
      <div class="table-container">
        <table class="metrics-table fixed-table align-middle text-center">
          <thead class="table-light">
            <tr>
              <th scope="col"></th>
              <th scope="col">Family History</th>
            </tr>
          </thead>
          <tbody>
            <!-- YES rows and expanded rows for Family History -->
            <ng-container
              *ngFor="let metric of groupedFamilyHistoryDonorMetrics.yes"
            >
              <tr>
                <td class="text-start ps-4 width-20pt">
                  {{ metric.label }}
                </td>
                <td class="background-light-yellow">
                  {{ metric.value }}
                </td>
              </tr>
            </ng-container>

            <!-- NO block (only if there are any) -->
            <ng-container
              *ngIf="groupedFamilyHistoryDonorMetrics.no.length > 0"
            >
              <ng-container
                *ngIf="getClearedNoMetrics(groupedFamilyHistoryDonorMetrics.no) as clearedFamilyNoMetrics"
              >
                <tr
                  *ngFor="
                    let metric of clearedFamilyNoMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="clearedFamilyNoMetrics.length"
                    class="text-center last-col"
                    style="background-color: #ecfef3"
                  >
                    None
                  </td>
                </tr>
              </ng-container>

              <ng-container
                *ngIf="getNotFilledNoMetrics(groupedFamilyHistoryDonorMetrics.no) as notFilledFamilyMetrics"
              >
                <tr
                  *ngFor="
                    let metric of notFilledFamilyMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="notFilledFamilyMetrics.length"
                    class="text-center last-col na-text"
                    style="background-color: #f2f2f2"
                  >
                    Not filled out
                  </td>
                </tr>
              </ng-container>
            </ng-container>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Social History Metrics Section -->
    <div class="metrics-section mt-4">
      <div class="table-container">
        <table class="metrics-table fixed-table align-middle text-center">
          <thead class="table-light">
            <tr>
              <th scope="col"></th>
              <th scope="col">Social History</th>
            </tr>
          </thead>
          <tbody>
            <!-- YES rows and expanded rows for Social History -->
            <ng-container
              *ngFor="let metric of groupedSocialHistoryDonorMetrics.yes"
            >
              <tr
                [class.clickable-row]="isMetricExpandable(metric.key)"
                (click)="
                  isMetricExpandable(metric.key)
                    ? toggleRowExpansion(metric.key)
                    : null
                "
              >
                <td class="text-start ps-4 width-20pt">
                  {{ metric.label }}
                </td>
                <td
                  [style.background-color]="
                    !isMedicalInsurance(metric.key)
                      ? '#fffacd'
                      : isMedicalInsurance(metric.key)
                        ? getMetricCellBackground(metric)
                        : ''
                  "
                  [style.color]="
                    isMedicalInsurance(metric.key)
                      ? getMetricCellColor(metric)
                      : ''
                  "
                >
                  {{ metric.value }}
                  <i
                    *ngIf="isMetricExpandable(metric.key)"
                    class="bi ms-2 align-middle row-expand-toggle"
                    [class.bi-chevron-down]="!isRowExpanded(metric.key)"
                    [class.bi-chevron-up]="isRowExpanded(metric.key)"
                    (click)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    (keydown.enter)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    (keydown.space)="$event.stopPropagation(); toggleRowExpansion(metric.key)"
                    role="button"
                    tabindex="0"
                  ></i>
                  <div
                    *ngIf="isRowExpanded(metric.key)"
                    class="mt-3 expanded-response-content"
                    (click)="$event.stopPropagation()"
                  >
                    <div
                      *ngIf="isLoadingResponses(metric.key)"
                      class="text-center"
                    >
                      <div
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></div>
                      Loading detailed responses...
                    </div>
                    <div
                      *ngIf="
                        !isLoadingResponses(metric.key) &&
                        getResponsesForMetric(metric.key).length > 0
                      "
                    >
                      <div class="table-responsive">
                        <table
                          class="table table-sm table-bordered social-history-subtable"
                        >
                          <thead class="table-light">
                            <tr>
                              <th class="subtable-question">Question</th>
                              <th class="subtable-answer">Answer</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              *ngFor="
                                let response of getResponsesForMetric(
                                  metric.key
                                )
                              "
                              [style.background-color]="
                                isAnswerYes(response.answerText)
                                  ? '#fff3cd'
                                  : ''
                              "
                            >
                              <td
                                class="subtable-question"
                                [style.color]="
                                  isMetricNoValue(response.answerText)
                                    ? '#929292'
                                    : ''
                                "
                              >
                                {{ response.questionText }}
                              </td>
                              <td
                                class="subtable-answer"
                                [style.color]="
                                  isMetricNoValue(response.answerText)
                                    ? '#929292'
                                    : ''
                                "
                              >
                                <div
                                  class="subtable-answer-text"
                                  [textContent]="
                                    getAnswerDisplayText(response) || 'N/A'
                                  "
                                ></div>

                                @if (canTranslateDetailedResponse(response)) {
                                  <div class="translation-tools">
                                    @if (
                                      getDetailedTranslationState(response)
                                        ?.translatedText
                                    ) {
                                      <div class="translated-answer">
                                        <div class="translated-answer-label">
                                          English translation
                                        </div>
                                        <div
                                          class="translated-answer-text"
                                          [textContent]="
                                            getDetailedTranslatedDisplayText(
                                              response
                                            )
                                          "
                                        ></div>
                                      </div>
                                    } @else {
                                      <button
                                        type="button"
                                        class="btn btn-sm translate-answer-btn"
                                        [disabled]="
                                          getDetailedTranslationState(response)
                                            ?.loading
                                        "
                                        (click)="
                                          translateDetailedResponse(response)
                                        "
                                      >
                                        @if (
                                          getDetailedTranslationState(response)
                                            ?.loading
                                        ) {
                                          Translating...
                                        } @else {
                                          Translate to English
                                        }
                                      </button>
                                    }

                                    @if (
                                      getDetailedTranslationState(response)
                                        ?.error
                                    ) {
                                      <div class="translation-error">
                                        {{
                                          getDetailedTranslationState(response)
                                            ?.error
                                        }}
                                      </div>
                                    }
                                  </div>
                                }
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      *ngIf="
                        !isLoadingResponses(metric.key) &&
                        getResponsesForMetric(metric.key).length === 0
                      "
                      class="text-muted"
                    >
                      No detailed responses available for this metric.
                    </div>
                  </div>
                </td>
              </tr>
            </ng-container>

            <!-- NO block (only if there are any) -->
              <ng-container
                *ngIf="groupedSocialHistoryDonorMetrics.no.length > 0"
            >
              <!-- Handle MedicalInsurance separately as individual rows -->
              <ng-container
                *ngFor="let metric of medicalInsuranceSocialHistoryNo; trackBy: trackByMetric"
              >
                <tr>
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    class="text-center last-col"
                    [style.background-color]="getMetricCellBackground(metric)"
                    [style.color]="getMetricCellColor(metric)"
                  >
                    {{ metric.value }}
                  </td>
                </tr>
              </ng-container>

              <!-- Handle other "no" metrics as grouped rows -->
              <ng-container
                *ngIf="nonMedicalInsuranceSocialHistoryNo.length > 0"
              >
                <ng-container
                  *ngIf="getClearedNoMetrics(nonMedicalInsuranceSocialHistoryNo) as clearedSocialNoMetrics"
                >
                  <tr
                    *ngFor="
                      let metric of clearedSocialNoMetrics;
                      let i = index; trackBy: trackByMetric
                    "
                  >
                    <td class="text-start ps-4 width-20pt">
                      {{ metric.label }}
                    </td>
                    <td
                      *ngIf="i === 0"
                      [attr.rowspan]="clearedSocialNoMetrics.length"
                      class="text-center"
                      style="background-color: #ecfef3"
                    >
                      None
                    </td>
                  </tr>
                </ng-container>

                <ng-container
                  *ngIf="getNotFilledNoMetrics(nonMedicalInsuranceSocialHistoryNo) as notFilledSocialMetrics"
                >
                  <tr
                    *ngFor="
                      let metric of notFilledSocialMetrics;
                      let i = index; trackBy: trackByMetric
                    "
                  >
                    <td class="text-start ps-4 width-20pt">
                      {{ metric.label }}
                    </td>
                    <td
                      *ngIf="i === 0"
                      [attr.rowspan]="notFilledSocialMetrics.length"
                      class="text-center na-text"
                      style="background-color: #f2f2f2"
                    >
                      Not filled out
                    </td>
                  </tr>
                </ng-container>
              </ng-container>
            </ng-container>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Dietary History Metrics Section -->
    <div class="metrics-section mt-4">
      <div class="table-container">
        <table class="metrics-table fixed-table align-middle text-center">
          <thead class="table-light">
            <tr>
              <th scope="col"></th>
              <th scope="col">Dietary and Nutritional Assessment</th>
            </tr>
          </thead>
          <tbody>
            <!-- YES rows and expanded rows for Dietary and Nutritional Assessment -->
            <ng-container
              *ngFor="let metric of groupedDietHistoryDonorMetrics.yes; trackBy: trackByMetric"
            >
              <tr>
                <td class="text-start ps-4 width-20pt">
                  {{ metric.label }}
                </td>
                <td class="background-light-yellow">
                  {{ metric.value }}
                </td>
              </tr>
            </ng-container>

            <!-- NO block (only if there are any) -->
            <ng-container *ngIf="groupedDietHistoryDonorMetrics.no.length > 0">
              <ng-container
                *ngIf="getClearedNoMetrics(groupedDietHistoryDonorMetrics.no) as clearedDietNoMetrics"
              >
                <tr
                  *ngFor="
                    let metric of clearedDietNoMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="clearedDietNoMetrics.length"
                    class="text-center last-col"
                    style="background-color: #ecfef3"
                  >
                    None
                  </td>
                </tr>
              </ng-container>

              <ng-container
                *ngIf="getNotFilledNoMetrics(groupedDietHistoryDonorMetrics.no) as notFilledDietMetrics"
              >
                <tr
                  *ngFor="
                    let metric of notFilledDietMetrics;
                    let i = index; trackBy: trackByMetric
                  "
                >
                  <td class="text-start ps-4 width-20pt">
                    {{ metric.label }}
                  </td>
                  <td
                    *ngIf="i === 0"
                    [attr.rowspan]="notFilledDietMetrics.length"
                    class="text-center last-col na-text"
                    style="background-color: #f2f2f2"
                  >
                    Not filled out
                  </td>
                </tr>
              </ng-container>
            </ng-container>
          </tbody>
        </table>
      </div>
    </div>
  </div>
}
